import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Alert,
  Button,
  Form,
  FormFeedback,
  Input,
  Label,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Spinner,
  Table,
} from "reactstrap";

import {
  datetimeLocalValueToUtcIso,
  formatUtcIsoForDisplay,
  localDateToUtcDayRange,
} from "../../../../helpers/datetimeUtc";
import {
  createProfessionalBlock,
  deleteProfessionalBlock,
  getProfessionalBlocks,
  Professional,
  ScheduleBlock,
} from "../../../../services/professionalService";

type AbsenceFormValues = {
  start_datetime: string;
  end_datetime: string;
  all_day_date: string;
  reason: string;
};

const emptyFormValues = (): AbsenceFormValues => ({
  start_datetime: "",
  end_datetime: "",
  all_day_date: "",
  reason: "",
});

interface ScheduleBlocksModalProps {
  professional: Professional | null;
  isOpen: boolean;
  onClose: () => void;
  initialRange?: { startLocal: string; endLocal: string } | null;
}

const ScheduleBlocksModal = ({
  professional,
  isOpen,
  onClose,
  initialRange = null,
}: ScheduleBlocksModalProps) => {
  const { t, i18n } = useTranslation();
  const [blocks, setBlocks] = useState<ScheduleBlock[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [allDay, setAllDay] = useState(false);
  const [formValues, setFormValues] = useState<AbsenceFormValues>(emptyFormValues);
  const [formError, setFormError] = useState<string | null>(null);

  const formatRange = useCallback(
    (block: ScheduleBlock): string => {
      const start = formatUtcIsoForDisplay(block.start_datetime, i18n.language);
      const end = formatUtcIsoForDisplay(block.end_datetime, i18n.language);

      return t("studio.professionals.blocks.range", { start, end });
    },
    [i18n.language, t]
  );

  const loadBlocks = useCallback(async () => {
    if (!professional) {
      return;
    }

    setLoading(true);

    try {
      const response = await getProfessionalBlocks(professional.id, { per_page: 50 });
      setBlocks(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("studio.professionals.blocks.toast.loadError"));
      setBlocks([]);
    } finally {
      setLoading(false);
    }
  }, [professional, t]);

  useEffect(() => {
    if (!isOpen || !professional) {
      return;
    }

    setFormValues({
      ...emptyFormValues(),
      start_datetime: initialRange?.startLocal ?? "",
      end_datetime: initialRange?.endLocal ?? "",
    });
    setAllDay(false);
    setFormError(null);
    loadBlocks();
  }, [initialRange?.endLocal, initialRange?.startLocal, isOpen, professional, loadBlocks]);

  const validateForm = (): boolean => {
    if (allDay) {
      if (!formValues.all_day_date) {
        setFormError(t("studio.professionals.blocks.validation.dateRequired"));
        return false;
      }

      return true;
    }

    if (!formValues.start_datetime || !formValues.end_datetime) {
      setFormError(t("studio.professionals.blocks.validation.timesRequired"));
      return false;
    }

    if (new Date(formValues.end_datetime) <= new Date(formValues.start_datetime)) {
      setFormError(t("studio.professionals.blocks.validation.endAfterStart"));
      return false;
    }

    return true;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!professional) {
      return;
    }

    setFormError(null);

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    const payload = allDay
      ? (() => {
          const range = localDateToUtcDayRange(formValues.all_day_date);

          return {
            start_datetime: range.start,
            end_datetime: range.end,
            reason: formValues.reason.trim() || null,
          };
        })()
      : {
          start_datetime: datetimeLocalValueToUtcIso(formValues.start_datetime),
          end_datetime: datetimeLocalValueToUtcIso(formValues.end_datetime),
          reason: formValues.reason.trim() || null,
        };

    try {
      const response = await createProfessionalBlock(professional.id, payload);
      toast.success(response.message || t("studio.professionals.blocks.toast.createSuccess"));
      setFormValues(emptyFormValues());
      setAllDay(false);
      await loadBlocks();
    } catch (error: any) {
      toast.error(error?.data || t("studio.professionals.blocks.toast.createError"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (block: ScheduleBlock) => {
    if (!professional) {
      return;
    }

    setDeletingId(block.id);

    try {
      const response = await deleteProfessionalBlock(professional.id, block.id);
      toast.success(response.message || t("studio.professionals.blocks.toast.deleteSuccess"));
      await loadBlocks();
    } catch (error: any) {
      toast.error(error?.data || t("studio.professionals.blocks.toast.deleteError"));
    } finally {
      setDeletingId(null);
    }
  };

  const title = useMemo(
    () =>
      professional
        ? t("studio.professionals.blocks.title", { name: professional.name })
        : t("studio.professionals.blocks.titleFallback"),
    [professional, t]
  );

  return (
    <Offcanvas isOpen={isOpen} toggle={onClose} direction="end" className="border-0">
      <OffcanvasHeader toggle={onClose}>{title}</OffcanvasHeader>
      <OffcanvasBody>
        <Alert color="info" className="py-2">
          <small>{t("studio.professionals.blocks.feedback")}</small>
        </Alert>

        <h6 className="mb-3">{t("studio.professionals.blocks.formTitle")}</h6>
        <Form onSubmit={handleSubmit} className="mb-4">
          <div className="mb-3">
            <Form check>
              <Input
                type="checkbox"
                id="absence-all-day"
                checked={allDay}
                onChange={(event) => {
                  setAllDay(event.target.checked);
                  setFormError(null);
                }}
              />
              <Label check htmlFor="absence-all-day" className="ms-1">
                {t("studio.professionals.blocks.allDay")}
              </Label>
            </Form>
          </div>

          {allDay ? (
            <div className="mb-3">
              <Label htmlFor="absence-all-day-date">{t("studio.professionals.blocks.date")}</Label>
              <Input
                id="absence-all-day-date"
                type="date"
                value={formValues.all_day_date}
                onChange={(event) => {
                  setFormValues((current) => ({
                    ...current,
                    all_day_date: event.target.value,
                  }));
                  setFormError(null);
                }}
                invalid={!!formError}
              />
            </div>
          ) : (
            <>
              <div className="mb-3">
                <Label htmlFor="absence-start">{t("studio.professionals.blocks.start")}</Label>
                <Input
                  id="absence-start"
                  type="datetime-local"
                  value={formValues.start_datetime}
                  onChange={(event) => {
                    setFormValues((current) => ({
                      ...current,
                      start_datetime: event.target.value,
                    }));
                    setFormError(null);
                  }}
                  invalid={!!formError}
                />
              </div>
              <div className="mb-3">
                <Label htmlFor="absence-end">{t("studio.professionals.blocks.end")}</Label>
                <Input
                  id="absence-end"
                  type="datetime-local"
                  value={formValues.end_datetime}
                  onChange={(event) => {
                    setFormValues((current) => ({
                      ...current,
                      end_datetime: event.target.value,
                    }));
                    setFormError(null);
                  }}
                  invalid={!!formError}
                />
              </div>
            </>
          )}

          <div className="mb-3">
            <Label htmlFor="absence-reason">{t("studio.professionals.blocks.reason")}</Label>
            <Input
              id="absence-reason"
              type="text"
              value={formValues.reason}
              maxLength={255}
              placeholder={t("studio.professionals.blocks.reasonPlaceholder")}
              onChange={(event) =>
                setFormValues((current) => ({
                  ...current,
                  reason: event.target.value,
                }))
              }
            />
          </div>

          {formError ? <FormFeedback className="d-block mb-2">{formError}</FormFeedback> : null}

          <Button color="primary" type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner size="sm" className="me-1" />
                {t("studio.professionals.blocks.saving")}
              </>
            ) : (
              t("studio.professionals.blocks.add_button")
            )}
          </Button>
        </Form>

        <h6 className="mb-3">{t("studio.professionals.blocks.listTitle")}</h6>
        {loading ? (
          <div className="text-center py-3">
            <Spinner color="primary">{t("studio.professionals.blocks.loading")}</Spinner>
          </div>
        ) : blocks.length === 0 ? (
          <p className="text-muted mb-0">{t("studio.professionals.blocks.empty_state")}</p>
        ) : (
          <div className="table-responsive">
            <Table size="sm" className="align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>{t("studio.professionals.blocks.period")}</th>
                  <th>{t("studio.professionals.blocks.reason")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {blocks.map((block) => (
                  <tr key={block.id}>
                    <td className="small">{formatRange(block)}</td>
                    <td className="small">{block.reason || "—"}</td>
                    <td className="text-end">
                      <Button
                        color="soft-danger"
                        size="sm"
                        disabled={deletingId === block.id}
                        onClick={() => handleDelete(block)}
                        title={t("studio.professionals.blocks.remove")}
                      >
                        {deletingId === block.id ? (
                          <Spinner size="sm" />
                        ) : (
                          <i className="ri-delete-bin-line" />
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default ScheduleBlocksModal;
